<script setup>
import { computed, reactive } from "vue";

const props = defineProps({
  transaction: {
    type: Object,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["cancel", "save"]);

const hasMatchedInvoice = computed(
  () => (props.transaction?.invoices ?? []).length > 0,
);
const formattedPaidIn = computed(() =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(props.transaction?.paid_in ?? 0) / 100),
);
const form = reactive({
  status: props.transaction?.review?.status ?? "",
  notes: props.transaction?.notes ?? props.transaction?.review?.notes ?? "",
});

const save = () => {
  emit("save", {
    status: hasMatchedInvoice.value ? null : form.status || null,
    notes: form.notes.trim() || null,
  });
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 px-4 py-8"
    role="dialog"
    aria-modal="true"
    aria-labelledby="income-review-title"
  >
    <form
      class="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
      @submit.prevent="save"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 id="income-review-title" class="text-lg font-semibold">
            Review Income Transaction
          </h2>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
            {{ transaction.description }} · {{ formattedPaidIn }}
          </p>
        </div>
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Close
        </button>
      </div>

      <div class="mt-5 space-y-4">
        <div v-if="!hasMatchedInvoice" class="space-y-1">
          <label class="text-xs uppercase text-slate-500">Outcome</label>
          <select v-model="form.status" class="input w-full">
            <option value="">Still needs an invoice match</option>
            <option value="out_of_scope">Out of scope</option>
            <option value="no_vat">No VAT — invoice not required</option>
            <option value="other">Other non-invoice income</option>
          </select>
          <p class="text-xs text-slate-500">
            Select “Still needs an invoice match” to reopen a resolved item.
          </p>
        </div>
        <div
          v-else
          class="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          This transaction is matched to an invoice. You can add a note here,
          but its VAT treatment continues to come from the matched invoice.
        </div>

        <div class="space-y-1">
          <label class="text-xs uppercase text-slate-500">Notes</label>
          <textarea
            v-model="form.notes"
            class="input w-full"
            rows="4"
            maxlength="2000"
            placeholder="For example: HMRC VAT refund; no sales invoice expected."
          ></textarea>
        </div>
      </div>

      <div class="mt-6 flex justify-end gap-2">
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Cancel
        </button>
        <button type="submit" class="btn accent" :disabled="saving">
          {{ saving ? "Saving…" : "Save Review" }}
        </button>
      </div>
    </form>
  </div>
</template>
